import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { HttpStatus } from 'config/Api.Config';
import { cloneDeep, merge, set } from 'lodash';
import ContractCancellationReasons from 'models/enums/ContractCancellationReasons';
import ContractCancellationTypes from 'models/enums/ContractCancellationTypes';
import ContractStatusCode from 'models/enums/ContractStatusCode';
import contractRefundSaveApi from 'models/sales/contractRefund/save/Api.ContractRefund.Save';
import contractSetStatusApi from 'models/sales/contract/setStatus/Api.Contract.SetStatus';
import contractCancellationSaveApi from 'models/sales/contractCancellation/save/Api.ContractCancellation.Save';
import { useState } from 'react';
import { SalesContractAction, useSalesContractProvider } from '../../Sales.Contract.Hooks';
import { ContractVoidCancellationForm } from '../cancellation/Contract.VoidCancellation';
import ContractRefundProcessDetails from './tabs/Contract.RefundProcess.Details';
import ContractRefundProcessRefund from './tabs/Contract.RefundProcess.Refund';

export type ContractRefundProcessForm = Api.Sales.ContractRefund.Save.IRequest & ContractVoidCancellationForm;
export default function ContractRefundProcess() {
  const { lang } = getLocales();
  const { dealerId } = useAuthStore();
  const { selected, listApi, pageRouter, setIsStatusChanging } = useSalesContractProvider();

  const [refundPricingInfo, setRefundPricingInfo] =
    useState<Api.Sales.ContractRefund.RefundPricingInfo.RefundPriceInfo>(undefined);

  return (
    <ModalForm<ContractRefundProcessForm>
      subcomponents={merge(cloneDeep(ThemeModalFormDefault), {
        modal: {
          page: {
            style: {
              pageWrapper: {
                ['--modalWidth']: 'var(--sizeXXL) * 1.5',
              },
            },
          },
        },
      })}
      dependencyArray={[selected]}
      title={lang.prRefund}
      default={{
        newStatus: ContractStatusCode.PAID_PENDING_REFUND,
        dealerId: dealerId,
        isLoyaltyPremiumApplied: false,
        areClaimDeductionsApplied: false,
      }}
      onCancel={() => pageRouter.updateParams({ id: selected.id.toString(), action: SalesContractAction.INFO })}
      onSubmit={async ({ newStatus, ...data }) => {
        setIsStatusChanging(true);

        const contractStatusRes = await contractSetStatusApi(parseInt(pageRouter.params.id), {
          newStatus,
          triggerStatusFlow: true,
        });
        if (contractStatusRes.status !== HttpStatus.ok) {
          setIsStatusChanging(false);
          addLog({ error: contractStatusRes.message });
          return;
        }

        const contractCancellationRes = await contractCancellationSaveApi({
          type: ContractCancellationTypes.Refund,
          contract: { id: contractStatusRes.payload, statusCode: newStatus },
          contractId: contractStatusRes.payload,
          odometerReading: data.odometerReading,
          cancellationDateLocal: data.cancellationDateLocal,
          reason: data.reason,
          reasonDescription: data.reasonDescription,
          refundDateAtLocal: data.refundDateAtLocal,
          refundPricingDistributions: undefined,
          refundDistributorDistributions: undefined,
          refundSubfees: undefined,
          refundEnhancements: undefined,
        });
        if (contractCancellationRes.status !== HttpStatus.ok) {
          setIsStatusChanging(false);
          addLog({ error: contractCancellationRes.message });
          return;
        }

        const contractRefundRes = await contractRefundSaveApi({
          ...data,
          contractCancellationId: contractCancellationRes.payload,
        });
        if (contractRefundRes.status !== HttpStatus.ok) {
          setIsStatusChanging(false);
          addLog({ error: contractRefundRes.message });
          return;
        }

        addLog({ success: lang.saveSuccess });
        pageRouter.updateParams();
        setIsStatusChanging(false);
        listApi.execute((b) => b);
      }}
      validation={(data, errors) => {
        if (!data?.cancellationDateLocal) set(errors, 'cancellationDateLocal', lang.mustNotBeEmpty);
        if (!data?.odometerReading) set(errors, 'odometerReading', lang.mustNotBeEmpty);
        if (!data?.reason) set(errors, 'reason', lang.mustNotBeEmpty);
        if (data?.reason && data?.reason === ContractCancellationReasons.OTHER && !data?.reasonDescription)
          set(errors, 'reasonDescription', lang.mustNotBeEmpty);

        if (data?.isLoyaltyPremiumApplied && !data.newAppNumber) set(errors, 'newAppNumber', lang.mustNotBeEmpty);

        if (!data?.refundDateAtLocal) set(errors, 'refundDateAtLocal', lang.mustNotBeEmpty);
        if (!data?.refundConfigTemplateId) set(errors, 'refundConfigTemplateId', lang.mustNotBeEmpty);
      }}
      isQuery={true}
      tabs={[
        {
          id: 'details',
          text: lang.details,
          errors: (props) => {
            return (
              props.form?.errors?.cancellationDateLocal ||
              props.form?.errors?.odometerReading ||
              props.form?.errors?.reason ||
              (props.form?.data?.reason === ContractCancellationReasons.OTHER &&
                props.form?.errors?.reasonDescription) ||
              (props.form?.data?.isLoyaltyPremiumApplied === true && props.form?.errors?.newAppNumber)
            );
          },
        },
        {
          id: 'refund',
          text: lang.refund,
          errors: (props) => props.form?.errors?.refundDateAtLocal || props.form?.errors?.refundConfigTemplateId,
        },
      ]}
      render={({ activeTab }) =>
        activeTab?.id === 'details' ? (
          <ContractRefundProcessDetails />
        ) : activeTab?.id === 'refund' ? (
          <ContractRefundProcessRefund
            refundPricingInfo={refundPricingInfo}
            setRefundPricingInfo={setRefundPricingInfo}
          />
        ) : null
      }
    />
  );
}
